import { useState, useRef } from 'react';
import { useDAWStore } from '../store/daw-store';
import type { Theme } from '../store/daw-store';
import { X, Check, Plus, Download, Upload, Trash2 } from 'lucide-react';
import { ThemeEditor } from './ThemeEditor';

interface ThemeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ThemeSelector({ isOpen, onClose }: ThemeSelectorProps) {
  const { themes, currentTheme, setTheme, exportTheme, importTheme, removeCustomTheme } = useDAWStore();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleThemeClick = (theme: Theme) => {
    setTheme(theme);
    onClose();
  };

  const handleCreateCustomTheme = () => {
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
  };

  const handleExportTheme = (themeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent theme selection
    exportTheme(themeId);
  };

  const handleImportClick = () => {
    setImportError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await importTheme(file);
      setImportError(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'Failed to import theme');
    }
  };

  const handleDeleteTheme = (themeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent theme selection
    if (confirm('Are you sure you want to delete this theme?')) {
      removeCustomTheme(themeId);
    }
  };

  const isCustomTheme = (themeId: string) => {
    return !['default', 'blue', 'purple'].includes(themeId);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-[var(--daw-bg-elevated)] border border-[var(--daw-border)] rounded-lg shadow-2xl w-[800px] max-h-[600px] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--daw-border)]">
            <h2 className="text-lg font-semibold text-white">Select Theme</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-[var(--daw-bg-tertiary)] rounded transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Theme Grid */}
          <div className="p-6 overflow-y-auto max-h-[500px]">
            {/* Action Buttons Row */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={handleCreateCustomTheme}
                className="flex-1 p-4 rounded-lg border-2 border-dashed border-[var(--daw-border)] bg-[var(--daw-bg-secondary)] hover:border-[var(--daw-accent-primary)] hover:bg-[var(--daw-bg-tertiary)] transition-all duration-200 flex items-center justify-center gap-2 text-gray-300 hover:text-white"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Create Custom</span>
              </button>
              
              <button
                onClick={handleImportClick}
                className="flex-1 p-4 rounded-lg border-2 border-dashed border-[var(--daw-border)] bg-[var(--daw-bg-secondary)] hover:border-[var(--daw-accent-primary)] hover:bg-[var(--daw-bg-tertiary)] transition-all duration-200 flex items-center justify-center gap-2 text-gray-300 hover:text-white"
              >
                <Upload className="w-5 h-5" />
                <span className="font-medium">Import Theme</span>
              </button>
            </div>

            {/* Hidden file input for import */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="hidden"
            />

            {/* Import Error Message */}
            {importError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {importError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {themes.map((theme: Theme) => (
                <ThemePreviewCard
                  key={theme.id}
                  theme={theme}
                  isActive={currentTheme.id === theme.id}
                  isCustom={isCustomTheme(theme.id)}
                  onClick={() => handleThemeClick(theme)}
                  onExport={(e) => handleExportTheme(theme.id, e)}
                  onDelete={(e) => handleDeleteTheme(theme.id, e)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Theme Editor Modal */}
      <ThemeEditor isOpen={isEditorOpen} onClose={handleEditorClose} />
    </>
  );
}

interface ThemePreviewCardProps {
  theme: Theme;
  isActive: boolean;
  isCustom: boolean;
  onClick: () => void;
  onExport: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}

function ThemePreviewCard({ theme, isActive, isCustom, onClick, onExport, onDelete }: ThemePreviewCardProps) {
  return (
    <button
      onClick={onClick}
      className={`
        relative p-4 rounded-lg border-2 transition-all duration-200
        ${isActive 
          ? 'border-[var(--daw-accent-primary)] bg-[var(--daw-bg-tertiary)]' 
          : 'border-[var(--daw-border)] bg-[var(--daw-bg-secondary)] hover:border-[var(--daw-border-light)] hover:bg-[var(--daw-bg-tertiary)]'
        }
      `}
    >
      {/* Active Indicator */}
      {isActive && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-[var(--daw-accent-primary)] rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )}

      {/* Action Buttons (Export/Delete) */}
      {!isActive && (
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={onExport}
            className="p-1.5 bg-[var(--daw-bg-elevated)] hover:bg-[var(--daw-accent-primary)] rounded border border-[var(--daw-border)] transition-colors"
            title="Export Theme"
          >
            <Download className="w-3.5 h-3.5 text-gray-400 hover:text-white" />
          </button>
          {isCustom && (
            <button
              onClick={onDelete}
              className="p-1.5 bg-[var(--daw-bg-elevated)] hover:bg-red-500 rounded border border-[var(--daw-border)] transition-colors"
              title="Delete Theme"
            >
              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-white" />
            </button>
          )}
        </div>
      )}

      {/* Theme Name */}
      <div className="text-left mb-3">
        <h3 className="text-sm font-semibold text-white">{theme.name}</h3>
      </div>

      {/* Color Swatches */}
      <div className="space-y-2">
        {/* Background Colors Row */}
        <div className="flex gap-1">
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.primary }}
            title="Primary Background"
          />
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.secondary }}
            title="Secondary Background"
          />
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.tertiary }}
            title="Tertiary Background"
          />
        </div>

        {/* Accent Colors Row */}
        <div className="flex gap-1">
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.accentPrimary }}
            title="Primary Accent"
          />
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.accentHover }}
            title="Hover Accent"
          />
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.accentActive }}
            title="Active Accent"
          />
        </div>

        {/* Border & Elevated Row */}
        <div className="flex gap-1">
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.elevated }}
            title="Elevated Background"
          />
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.border }}
            title="Border Color"
          />
          <div
            className="flex-1 h-8 rounded border border-white/10"
            style={{ backgroundColor: theme.colors.borderLight }}
            title="Light Border"
          />
        </div>
      </div>

      {/* Color Labels */}
      <div className="mt-3 text-xs text-gray-400 space-y-1">
        <div className="flex justify-between">
          <span>Backgrounds</span>
          <span>Accents</span>
        </div>
      </div>
    </button>
  );
}
